import {useEffect, useRef, useState} from 'react';
import * as THREE from 'three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader.js';
import {RGBELoader} from 'three/examples/jsm/loaders/RGBELoader.js';
import {DRACOLoader} from 'three/examples/jsm/loaders/DRACOLoader.js';
import {KTX2Loader} from 'three/examples/jsm/loaders/KTX2Loader.js';
import {OrbitControls} from 'three/examples/jsm/controls/OrbitControls.js';

export function useThreeFiberViewer(
    canvasRef,
    modelPath,
    options = {}
) {
    const {
        hdrPath = '/assets/hdr/studio_small_09_1k.hdr',
        autoOrbit = true,
        orbitRadius = 6,
        orbitHeight = 2,
        background = null,
        exposure = 1.0,
        useOrbitControls = false,
    } = options;

    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const pmremRef = useRef(null);
    const rafRef = useRef(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !modelPath) return;

        // ---------------- Scene / Camera / Renderer ----------------
        const scene = new THREE.Scene();
        if (background) scene.background = background;
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(
            50,
            canvas.clientWidth / Math.max(1, canvas.clientHeight),
            0.1,
            1000
        );
        camera.position.set(4, 2, 4);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({
            canvas,
            alpha: !background,
            antialias: true,
            powerPreference: 'high-performance',
        });
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = exposure;
        renderer.physicallyCorrectLights = true;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        rendererRef.current = renderer;

        const setSize = () => {
            const w = Math.max(1, canvas.clientWidth);
            const h = Math.max(1, canvas.clientHeight);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            renderer.setSize(w, h, false);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
        };
        setSize();

        const ro = new ResizeObserver(setSize);
        ro.observe(canvas);

        // ---------------- Luces tipo estudio ----------------
        scene.add(new THREE.AmbientLight(0xffffff, 0.35));

        const sun = new THREE.DirectionalLight(0xffffff, 2.5);
        sun.position.set(5, 8, 5);
        sun.castShadow = true;
        sun.shadow.mapSize.set(2048, 2048);
        Object.assign(sun.shadow.camera, {
            near: 0.5, far: 40, left: -10, right: 10, top: 10, bottom: -10,
        });
        sun.shadow.bias = -0.0001;
        sun.shadow.normalBias = 0.04;
        scene.add(sun);

        // ---------------- Environment HDRI ----------------
        const pmrem = new THREE.PMREMGenerator(renderer);
        pmremRef.current = pmrem;

        const loadHDR = hdrPath
            ? new RGBELoader().loadAsync(hdrPath).then((hdr) => {
                scene.environment = pmrem.fromEquirectangular(hdr).texture;
                hdr.dispose();
            })
            : Promise.resolve();

        // ---------------- OrbitControls opcional ----------------
        let controls = null;
        if (useOrbitControls) {
            controls = new OrbitControls(camera, renderer.domElement);
            controls.enableDamping = true;
            controls.enablePan = false;
            controls.autoRotate = true;
            controls.autoRotateSpeed = 1.0;
            controls.target.set(0, 0, 0);
        }

        // ---------------- Cargar GLB (DRACO/KTX2 opcional) ----------------
        const loader = new GLTFLoader();
        const draco = new DRACOLoader().setDecoderPath('/libs/draco/');
        loader.setDRACOLoader(draco);
        const ktx2 = new KTX2Loader().setTranscoderPath('/libs/basis/').detectSupport(renderer);
        loader.setKTX2Loader(ktx2);

        let ground = null;

        const loadGLB = loader
            .loadAsync(modelPath)
            .then((gltf) => {
                const model = gltf.scene;

                model.traverse((o) => {
                    if (!o.isMesh) return;
                    o.castShadow = true;
                    o.receiveShadow = true;

                    if (o.material && o.material.map) {
                        o.material.map.colorSpace = THREE.SRGBColorSpace;
                    }

                    // Materiales PBR por defecto si vienen "planos"
                    const name = ((o.material && o.material.name) || o.name || '').toLowerCase();
                    if (name.includes('gelcoat') || name.includes('interior') || name.includes('1_blue')) {
                        o.material = new THREE.MeshPhysicalMaterial({
                            color: 0x1e5ad6,
                            roughness: 0.35,
                            metalness: 0.0,
                            clearcoat: 1.0,
                            clearcoatRoughness: 0.12,
                        });
                    } else if (name.includes('exterior') || name.includes('negro') || name.includes('costilla')) {
                        o.material = new THREE.MeshStandardMaterial({
                            color: 0x606060,
                            roughness: 1,
                            metalness: 0.0,
                        });
                    }

                    // --- FIX de transparencia (aplica a todos) ---
                    if (o.material) {
                        o.material.transparent = false;
                        o.material.opacity = 1;
                        o.material.depthWrite = true;
                        o.material.depthTest = true;
                        o.material.alphaTest = 0;
                        o.material.side = THREE.DoubleSide; // si podés corregir normales en Blender, usá FrontSide
                        o.material.needsUpdate = true;
                    }
                });

                // Fit-to-box + centrar
                const box = new THREE.Box3().setFromObject(model);
                const size = box.getSize(new THREE.Vector3());
                const center = box.getCenter(new THREE.Vector3());
                model.position.sub(center);

                const maxDim = Math.max(size.x, size.y, size.z);
                const fitDist = maxDim / (2 * Math.tan((camera.fov * Math.PI) / 360));
                const dist = 1.25 * fitDist;
                camera.position.set(dist, dist * 0.5, dist);

                // Piso receptor de sombras (ancla visual)
                const shadowMat = new THREE.ShadowMaterial({ opacity: 0.28 });
                ground = new THREE.Mesh(new THREE.PlaneGeometry(50, 50), shadowMat);
                ground.rotation.x = -Math.PI / 2;
                ground.position.y = -size.y * 0.5 - 0.005;
                ground.receiveShadow = true;

                scene.add(ground);
                scene.add(model);
            });

        // ---------------- Loop de render (órbita de cámara) ----------------
        let angle = 0;
        const renderLoop = () => {
            rafRef.current = requestAnimationFrame(renderLoop);

            if (useOrbitControls && controls) {
                controls.update();
            } else if (autoOrbit) {
                angle += 0.003;
                camera.position.x = Math.cos(angle) * orbitRadius;
                camera.position.z = Math.sin(angle) * orbitRadius;
                camera.position.y = orbitHeight;
                camera.lookAt(0, 0, 0);
            }

            renderer.render(scene, camera);
        };

        Promise.all([loadHDR, loadGLB])
            .then(() => setLoading(false))
            .then(renderLoop)
            .catch((e) => {
                setError(e);
                setLoading(false);
            });

        // ---------------- Cleanup ----------------
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            ro.disconnect();
            if (controls) controls.dispose();
            renderer.dispose();
            if (pmrem) pmrem.dispose();

            // liberar recursos de geometrías/materiales
            scene.traverse((obj) => {
                if (obj.isMesh) {
                    if (obj.geometry && obj.geometry.dispose) obj.geometry.dispose();
                    if (Array.isArray(obj.material)) {
                        obj.material.forEach((m) => m && m.dispose && m.dispose());
                    } else if (obj.material && obj.material.dispose) {
                        obj.material.dispose();
                    }
                }
            });
            if (ground) {
                if (ground.geometry && ground.geometry.dispose) ground.geometry.dispose();
                if (ground.material && ground.material.dispose) ground.material.dispose();
            }
        };
    }, [
        canvasRef,
        modelPath,
        hdrPath,
        autoOrbit,
        orbitRadius,
        orbitHeight,
        background,
        exposure,
        useOrbitControls,
    ]);

    return { loading, error };
}
